import React, { useEffect, useRef, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import _clonedeep from 'lodash/cloneDeep';
import Carousel from './Carousel';
import moment from 'moment';
import { signInCheck } from '../../../components/common/CheckUserStatus';
import properties from '../../../modules/ajio-util/properties';
import { gtmEventPush, getStoreName } from '../../../modules/ga/GTMdatalayer';
import { getFieldsFromDateAsFormat } from '../../../utils/dateFormatter';
import {
  getAjioSuperCashBottomStripConfig,
  getAjioSuperCashCurrencyConfig,
  getAjioSuperCashBottomStripConfigPLP,
  getAjioSuperCashBottomStripConfigPDP
} from '../../selectors/featureFlagsSelector';
import styles from './index.pcss';
import { replaceWithJioCDN } from '../../../utils/imageHandling';

const _getExpiringString = (expiryDate, expiringSuperCashDays = '', minimumDaysDateThresold) => {
  const now = Date.now();
  const pendingDays = Math.floor((expiryDate - now) / (24 * 60 * 60 * 1000));

  if (Number(expiringSuperCashDays) < pendingDays) {
    return '';
  }

  if (pendingDays < 0) return '';
  if (pendingDays === 0) return 'today';
  if (pendingDays === 1) return `in 1 day`;
  if (pendingDays < Number(minimumDaysDateThresold)) return `in ${pendingDays} days`;

  const { dd = '', month3Val = '', yyyy = '' } = getFieldsFromDateAsFormat(expiryDate) || {};

  return `on ${dd} ${month3Val} '${String(yyyy).slice(-2)}`;
};

function getLatestExpiringPoints(
  topExpiringPoints = [],
  expiringSuperCashDays = '',
  minimumDaysDateThresold = ''
) {
  if (!Array.isArray(topExpiringPoints) || topExpiringPoints.length === 0) {
    return { amount: null, expiringString: '', expiryDate: '' };
  }

  const now = new Date();
  const nonExpired = topExpiringPoints.filter(item => now.valueOf() < item?.expiryDate);

  if (nonExpired.length === 0) {
    return { amount: null, expiringString: '' };
  }

  const latestExpiring = nonExpired.sort((a, b) => a?.expiryDate - b?.expiryDate);
  const latestTopExpiring = latestExpiring[0];
  const expiringString = _getExpiringString(
    latestTopExpiring?.expiryDate,
    expiringSuperCashDays,
    minimumDaysDateThresold
  );
  const formattedDate = latestTopExpiring?.expiryDate && moment(latestTopExpiring.expiryDate).isValid() 
  ? moment(latestTopExpiring.expiryDate).format('YYYY-MM-DD') 
  : '';

  return latestTopExpiring && expiringString
    ? { amount: latestTopExpiring?.amount, expiringString,  expiryDate: formattedDate }
    : { amount: null, expiringString: '' ,  expiryDate: ''};
}

function formatIfDecimal(value) {
  return value % 1 !== 0 ? Math.floor(value * 100) / 100 : value;
}


function processSlides(
  slides,
  isSignedIn,
  userTotalSuperCashBalance,
  minimumSuperCashBalance,
  userSuperCashExpiringPoints,
  userSuperCashExpiringDate,
  expiringSuperCashBalance
) {
  const updatedSlides = _clonedeep(slides);

  // First, sort the slides by priority
  updatedSlides.sort((a, b) => a.priority - b.priority);

  return updatedSlides
    .filter(slide => {
      // Filter based on user type
      if (isSignedIn) {
        return slide.userType === 'loggedin' || slide.userType === 'all';
      } else {
        return slide.userType === 'guest' || slide.userType === 'all';
      }
    })
    .filter(slide => {
      // Replace super cash placeholders and apply additional filtering
      if (slide?.template?.includes('{super_cash}')) {
        if (
          Number(userTotalSuperCashBalance) <= 0 ||
          Number(minimumSuperCashBalance) > Number(userTotalSuperCashBalance)
        ) {
          return false;
        }
        slide.template = slide.template.replace('{super_cash}', formatIfDecimal(Number(userTotalSuperCashBalance)));
      } 

      if (slide?.template?.includes('{expire_super_cash}')) {
        if (
          Number(expiringSuperCashBalance) > Number(userSuperCashExpiringPoints) ||
          !userSuperCashExpiringDate || 
          Number(userSuperCashExpiringPoints) <= 0
        ) {
          return false;
        }
        slide.template = slide.template.replace('{expire_super_cash}', formatIfDecimal(Number(userSuperCashExpiringPoints)));
        slide.template = slide.template.replace('{expiry_date}', userSuperCashExpiringDate);
      }

      return true;
    });
}

const BottomStrip = ({
  bottomStripConfig = {},
  ajioWalletData = {},
  superCashCurrency = {},
  page = '',
  bottomStripConfigPLP = {},
  bottomStripConfigPDP = {}
}) => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const isSignedIn = signInCheck().length > 0;
  const storeName = getStoreName();

  const arrowIcon = `${properties.getProperty(
    'imageBaseUrl'
  )}/static/img/arrow_right_supercash.svg`;
  const featureLogo = superCashCurrency?.currencyPrimaryLogo || '';

  const getBottomStripConfig = () => {
    if (page.toLocaleLowerCase() === 'plp') {
      return bottomStripConfigPLP || {};
    } else if (page.toLocaleLowerCase() === 'pdp') {
      return bottomStripConfigPDP || {};
    }
    return bottomStripConfig;
  };

  const selectedBottomStripConfig = getBottomStripConfig(
    page,
    bottomStripConfigPLP,
    bottomStripConfigPDP
  );

  const {
    autoScrollTimer = '3',
    minimumSuperCashBalance = '0',
    expiringSuperCashBalance = '0',
    expiringSuperCashDays = '0',
    indicatorBackground = '#C4C4C4',
    indicatorForeground = '#2C4152',
    minimumDaysDateThresold = '10',
    maximumMessageLength = '100',
    enableSticky = 'OFF',
  } = selectedBottomStripConfig;

  const { topExpiringPoints = [], totalWalletBalance = {} } =
    ajioWalletData?.ajioWalletBalanceDetails || {};

  const { totalPoints = 0 } = totalWalletBalance;

  const userTotalSuperCashBalance = totalPoints || 0;
  const {
    amount: userSuperCashExpiringPoints,
    expiringString: userSuperCashExpiringDate,
    expiryDate: userSuperCashExpiringDateFormatted
  } = getLatestExpiringPoints(topExpiringPoints, expiringSuperCashDays, minimumDaysDateThresold);

  useEffect(() => {
    if (isSignedIn && Array.isArray(slidesArray) && slidesArray.length > 0) {
      gtmEventPush({
        event: 'ajio_super_cash_interactions',
        event_category: 'Ajio Super Cash Interactions',
        event_action: 'bottom_banner_view',
        event_label: slidesArray[0]?.template || '',
        supercash_expiry_date: userSuperCashExpiringDateFormatted,
        redirect_url :  slidesArray[0]?.redirectionUrl || '',
        supercash_balance: userTotalSuperCashBalance,
        supercash_expiring: userSuperCashExpiringPoints,
      });
    } else {
      gtmEventPush({
        event: 'ajio_super_cash_interactions',
        event_category: 'Ajio Super Cash Interactions',
        event_action: 'bottom_banner_view',
      });
    }
  }, [userTotalSuperCashBalance, userSuperCashExpiringPoints, userSuperCashExpiringDate, slidesArray]);

  const slidesArray = processSlides(
    selectedBottomStripConfig?.slides || [],
    isSignedIn,
    userTotalSuperCashBalance,
    minimumSuperCashBalance,
    userSuperCashExpiringPoints,
    userSuperCashExpiringDate,
    expiringSuperCashBalance
  );

  const activeSlide = useMemo(() => {
    return slidesArray && Array.isArray(slidesArray) ? slidesArray[activeSlideIndex] : {};
  }, [slidesArray, activeSlideIndex]);
  
  const dynamicBackground = useMemo(() => {
    const startColor = activeSlide?.stripGradientStartColor || '#DAE7FF';
    const endColor = activeSlide?.stripGradientEndColor || '#F4F8FF';
    return `linear-gradient(90deg, ${startColor} 0%, ${endColor} 100%)`;
  }, [activeSlide]);
  
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);

  const openInNewTab = (url = '', bannerText = '') => {
    if (url && url.length > 0) {
      if (isSignedIn) {
        gtmEventPush({
          event: 'ajio_super_cash_interactions',
          event_category: 'Ajio Super Cash Interactions',
          event_action: 'bottom_banner_click',
          event_label: bannerText,
          supercash_expiry_date: userSuperCashExpiringDateFormatted,
          redirect_url: url,
          supercash_balance: userTotalSuperCashBalance,
          supercash_expiring: userSuperCashExpiringPoints
        });
      } else {
        gtmEventPush({
          event: 'ajio_super_cash_interactions',
          event_category: 'Ajio Super Cash Interactions',
          event_action: 'bottom_banner_click',
          event_label: bannerText,
          redirect_url: url
        });
      }
      window.location.href = url;
    }
  };

  const handleScroll = () => {
   if (enableSticky === 'OFF') {
      const currentScrollY = window.scrollY;
  
      // Hide when scrolling down
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setIsVisible(false);
      } 
      // Show only when scrolled to the top
      else if (currentScrollY === 0) {
        setIsVisible(true);
      }
      lastScrollY.current = currentScrollY;
   }
  };
  
  useEffect(() => {
    if (enableSticky === 'OFF') {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
    return;
  }, []);
  

  const bottomStripStyles = {
    opacity: isVisible ? 1 : 0,
    visibility: isVisible ? 'visible' : 'hidden',
    height: isVisible ? 'auto' : '0px',
    overflow: 'hidden',
    ...(page === 'home' && {
      position: 'fixed',
      bottom: 56,
      width: '100%',
      transition: 'transform 0.3s ease-in-out',
      transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
      zIndex: 100
    }),
    ...(page === 'pdp' && {
      position: 'sticky',
      bottom: 62,
      width: '100%',
      transition: 'transform 0.3s ease-in-out',
      transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
    }),
    ...(page === 'plp' && {
      position: 'fixed',
      bottom: 40,
      width: '100%',
      transition: 'transform 0.3s ease-in-out',
      transform: isVisible ? 'translateY(0)' : 'translateY(170%)',
      zIndex: 100
    }),
    // ...(page === 'pdp' && { position: 'sticky', bottom: 62, width: '100%' }),
    // ...(page === 'plp' && { position: 'sticky', bottom: 40, width: '100%' }),
    // zIndex:1
  };

  return (
    slidesArray.length > 0 && (
      <div className={styles.bottomStrip}  style={{ ...bottomStripStyles, background: dynamicBackground }} id="bottomStripSlider">
        <Carousel
          interval={Number(autoScrollTimer) * 1000}
          barActiveColor={indicatorForeground}
          barInactiveColor={indicatorBackground}
          onSlideChange={setActiveSlideIndex}
          dynamicBackground={dynamicBackground}
          hideFocusToIndicatorAndListBehavior={page === "plp" || page === "home" || page === "pdp" }
        >
          {slidesArray.map((item, index) => {
            const maxLength = Number(maximumMessageLength);
            let truncatedText = item.template;
            if (truncatedText.length > maxLength) {
              truncatedText = truncatedText.substring(0, maxLength).trim() + '...';
            }
            return (
              <div
                key={index}
                className={styles.slideContent}
                style={{
                  color: item?.stripTextColor,
                  background: `linear-gradient(90deg, ${item?.stripGradientStartColor} 0%, ${item?.stripGradientEndColor} 100%)`,
                  padding: isVisible ? '10px 15px 5px' : '0',
                  width: '100%'
                }}
                onClick={() => openInNewTab(item.redirectionUrl, truncatedText)} 
                role="button" 
                tabIndex={0}
              >
                <div className={styles.iconLeft}>
                  <img src={replaceWithJioCDN(item?.slideLogo)} alt="SuperCash" aria-hidden="true"/>
                </div>
                <div className={styles.textContainer} style={{ color: item?.stripTextColor || '#22419E' }}>
                  {truncatedText
                    .split(/(₹[\d,]+(?:\.\d{1,2})?\s*supercash)/gi)
                    .map((part, idx) =>
                      /₹[\d,]+(?:\.\d{1,2})?\s*supercash/i.test(part) ? (
                        <strong key={idx}>{part}</strong>
                      ) : (
                        part
                      )
                    )}
                </div>
                {item?.redirectionUrl && (
                  <div
                    className={styles.iconRight}
                    aria-hidden="true"
                  >
                    <img src={arrowIcon} alt="arrow" />
                  </div>
                )}
              </div>
            );
          })}
        </Carousel>
      </div>
    )
  );
};

const mapStateToProps = state => {
  const { ajioWalletDataBottomStrip = {} } = state;
  return {
    bottomStripConfig: getAjioSuperCashBottomStripConfig(state?.flags),
    bottomStripConfigPLP: getAjioSuperCashBottomStripConfigPLP(state?.flags),
    bottomStripConfigPDP: getAjioSuperCashBottomStripConfigPDP(state?.flags),
    ajioWalletData: ajioWalletDataBottomStrip,
    superCashCurrency: getAjioSuperCashCurrencyConfig(state?.flags)
  };
};

export default connect(mapStateToProps)(BottomStrip);
